// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, IntlContext, IntlShape } from "react-intl";
import { Link } from "react-router-dom";

import { MainPanel, DashboardPanel, TimePeriod } from "types";
import { formatDate, getSiteSelection } from "guiutils";
import { IState } from "states/states";
import { PowerViewMode } from "states/cachedstates";
import { EnergySource, IEnergyMeter, ITariffCircuit } from "shared/types/parameter";
import { isSet } from "shared/utils/utils";

import TariffCircuitListModel from "model/tariffcircuitlist";

import PowerHeading from "view/comp/PowerHeading";
import TariffCircuitList from "view/comp/TariffCircuitList";
import PowerPieChart from "view/comp/PowerPieChart";
import SankeyChart from "view/comp/SankeyChart";
import HierarchyChart from "view/comp/HierarchyChart";

interface IPowerExtProps {
  panel?: DashboardPanel;
}

interface IPowerProps {
  // tariffCircuits: ITariffCircuit[];
  energyMeters: IEnergyMeter[];
  selectedEnergySource: EnergySource | undefined;
  powerViewMode: PowerViewMode;
  tariffCircuits: ITariffCircuit[];
  isHierarchyViewMode: boolean;
  trendMember: "energy" | "power" | "costs";
  selectedTimestamp: number | undefined;
  timePeriod: TimePeriod;
}

const mapStateToProps = (state: IState, ownProps: IPowerExtProps) => {
  const selection = getSiteSelection(state);
  const tariffCircuits = state.para.parameter?.tariffline ?? [];
  const defaultHierarchyViewMode = TariffCircuitListModel.hasHierarchy(tariffCircuits);

  const props: IPowerProps = {
    energyMeters: state.para.parameter?.energymeter ?? [],
    selectedEnergySource: selection?.energySource,
    powerViewMode: selection?.powerViewMode ?? PowerViewMode.TABLE,
    tariffCircuits: tariffCircuits,
    isHierarchyViewMode: selection?.isHierarchyViewMode ?? defaultHierarchyViewMode,
    trendMember: ownProps.panel === DashboardPanel.ENERGY ? state.gui.trendMember : "power",
    selectedTimestamp: state.gui.selectedTimestamp,
    timePeriod: state.gui.trendPeriod.timePeriod,
  };
  return props;
};

class PowerComponent extends React.Component<IPowerProps & IPowerExtProps> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const viewModes = [PowerViewMode.TABLE, PowerViewMode.HIERARCY, PowerViewMode.SANKEY];
    // if hierarchyViewMode is active -> also show PIE-Chart - no PIE-Chart in Normal-Mode
    if (this.props.isHierarchyViewMode) {
      viewModes.push(PowerViewMode.PIE);
    }
    return (
      <div>
        <div id="power">
          <div className="d-flex justify-content-between align-items-center mb-2 mx-2">
            <h1>
              <FormattedMessage id={`TrendMember_${this.props.trendMember}`} />
              {isSet(this.props.selectedTimestamp) ?
                <>{`: ${formatDate(
                  this.props.selectedTimestamp! * 1000,
                  this.props.timePeriod,
                  this.context.locale,
                  true,
                )}`}</>
              : ""}
            </h1>
            <PowerHeading viewModes={viewModes} panel={this.props.panel} />
          </div>
          {this.props.powerViewMode === PowerViewMode.TABLE && (
            <TariffCircuitList panel={this.props.panel} />
          )}
          {this.props.powerViewMode === PowerViewMode.PIE && (
            <PowerPieChart panel={this.props.panel} />
          )}
          {this.props.powerViewMode === PowerViewMode.SANKEY && (
            <SankeyChart panel={this.props.panel} />
          )}
          {this.props.powerViewMode === PowerViewMode.HIERARCY && (
            <HierarchyChart panel={this.props.panel} />
          )}
        </div>
        {this.props.panel !== DashboardPanel.ENERGY &&
          this.props.powerViewMode === PowerViewMode.TABLE &&
          this.renderEnergymeterLink()}
      </div>
    );
  }

  private renderEnergymeterLink() {
    return (
      <div className="mt-3">
        <FormattedMessage id="Click here to see the list of energy meters" />:
        <Link to={`../${MainPanel.ENERGYMETERS}`} className="ms-2">
          <FormattedMessage id="Energy meters" />
        </Link>
      </div>
    );
  }
}
export default connect(mapStateToProps)(PowerComponent);
